import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  RadioGroup,
  Radio,
  TextField,
  Typography,
  Button,
  FormControl,
  IconButton,
  CircularProgress,
  alpha,
  Chip,
} from "@mui/material";

import {
  Add,
  Close,
  Edit,
  Error,
  NavigateBefore,
  Print,
} from "@mui/icons-material";
import useApi from "_api/index";
import TableData from "components/Car/Table";
import RenderConditionDetail from "components/Car/RenderConditionDetail";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import { useNavigate, useParams } from "react-router-dom";
import ModalCreateTransferAndReceiptBuy from "./FormDocuments/TransferAndReceiptBuy";
import ModalPowerOfAttorney from "./FormDocuments/PowerOfAttorney";
import ModalContractPawnCar from "./FormDocuments/ContractPawn";
import { useCarStatuses } from "contexts/CarStatusContext";
import ChipCustomize from "components/Chip";
import { formatDateMonthYear, formatDateTime } from "utils/helpers/format";
import WatermarkedImage from "components/Pawn/WatermarkImage";
const fields = [
  {
    label: "คำนำหน้า",
    name: "prefix",
    type: "text",
    required: true,
  },
  {
    label: "ชื่อ-นามสกุล",
    name: "name",
    type: "text",
    required: true,
  },
  {
    label: "เลขบัตรประชาชน/ Passport No.",
    name: "idcard",
    type: "text",
    required: true,
  },
  {
    label: "เบอร์โทรศัพท์",
    name: "phonenumber",
    type: "text",
    required: true,
  },
  { label: "ที่อยู่", name: "address", type: "text", required: true },
  {
    label: "สัญชาติ",
    name: "nationality",
    type: "text",
    required: true,
    defaultValue: "ไทย",
  },
];

const ModalDetailCustomer = ({ open, onClose, rowID, type, formlist = [] }) => {
  const navigate = useNavigate();
  const { carStatuses } = useCarStatuses();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isEditDocuments, setIsEditDocuments] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const fetchDataDetail = async (rowID) => {
    try {
      setLoading(true);
      const response = await useApi.customer_car.getCustomerCarById(rowID);
      if ((response.success || response.isStatus) && response.data) {
        const { data } = response;
        setDetail(data);
      } else {
        setDetail(null);
        throw new Error("Failed to fetch car details");
      }
    } catch (error) {
      setDetail(null);
      console.error("Error fetching car details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (open && rowID) {
      fetchDataDetail(rowID);
    } else {
      setDetail(null);
      setLoading(true);
    }
  }, [open, rowID]);

  if (!detail && !loading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            padding: 2,
            borderRadius: 2,
            gap: 2,
          },
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: colors.grey[700],
          }}
        >
          <Close />
        </IconButton>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
            gap: 2,
          }}
        >
          <Error sx={{ fontSize: 48, color: colors.red[500] }} />
          <Typography variant="h6" color="textSecondary">
            เกิดข้อผิดพลาดในการดึงข้อมูลรถ
          </Typography>
          <Typography variant="body1" color="textSecondary">
            กรุณาลองอีกครั้งในภายหลัง
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (loading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        PaperComponent={() => null} // ไม่ render กล่อง paper
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.5)", // ตั้งสีพื้นหลัง
          },
        }}
      >
        {/* CircularProgress วางข้างนอก */}
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1301, // สูงกว่ backdrop
          }}
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          padding: 2,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h4">รายชื่อลูกค้า</Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: colors.grey[700],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // padding: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 4,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {detail?.customer && (
            <Box>
              <Typography variant="h5" sx={{ mb: 1 }}>
                ข้อมูลลูกค้า
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${colors.grey[300]}`,
                }}
              >
                <Grid container>
                  <Grid item xs={7}>
                    {fields.map((field, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        sx={{
                          m: 1,
                        }}
                        key={index}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1" sx={{ width: "200px" }}>
                            {field.label}
                            {field.required && (
                              <span style={{ color: "red" }}>*</span>
                            )}
                          </Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={detail ? detail.customer[field.name] : ""}
                            InputProps={{
                              readOnly: true,
                            }}
                            disabled
                            multiline={field.name === "customer_address"}
                            rows={field.name === "customer_address" ? 2 : 1}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                bgcolor: "white",
                                height:
                                  field.name === "customer_address"
                                    ? "auto"
                                    : "40px",
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Grid item xs={5}>
                    <Box
                      sx={{
                        p: 1,
                        width: "100%",
                        height: "214px",
                        borderRadius: 3,
                      }}
                    >
                      {detail && detail.customer.customer_image ? (
                        <WatermarkedImage
                          src={detail.customer.customer_image}
                          alt="Customer"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 3,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#F8F7FD",
                            color: "#B0B0B0",
                            borderRadius: 3,
                          }}
                        >
                          ไม่มีรูปภาพ
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h5" sx={{ mb: 1 }}>
              รายการ
            </Typography>
            <TableData
              columns={[
                {
                  key: "index",
                  label: "ลำดับ",
                  render: (row, index) => index + 1,
                },
                { key: "order_code", label: "รหัสรายการ" },
                { key: "car_price", label: "จำนวนเงิน" },
                {
                  key: "car_status_id",
                  label: "สถานะ",
                  render: (row) => (
                    <ChipCustomize
                      label={
                        carStatuses.find(
                          (status) => status.id === row.car_status_id
                        )?.name || "-"
                      }
                      status={
                        carStatuses.find(
                          (status) => status.id === row.car_status_id
                        )?.name
                      }
                    />
                  ),
                },
                {
                  key: "car_product_type",
                  label: "ประเภท",
                  render: (row) => (
                    <ChipCustomize status={row.car_product_type} />
                  ),
                },
                {
                  key: "created_date",
                  label: "วันที่บันทึก",
                  render: (row) => formatDateMonthYear(row.created_date),
                },
                { key: "latest_date", label: "วันที่ล่าสุด" },
              ]}
              data={detail ? detail.orders : []}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          paddingx: 2,
          mt: 1,
          borderTop: `1px solid ${colors.grey[300]}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderRadius: 2,
            minWidth: 100,
            borderColor: colors.grey[400],
            color: colors.grey[400],
            "&:hover": {
              borderColor: colors.grey[400],
              color: colors.grey[400],
              borderWidth: 2,
              bgcolor: "transparent",
            },
          }}
        >
          กลับ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalDetailCustomer;
